import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import WCCarousel from '../WCCarousel/WCCarousel';
import WCMatchDetails from '../WCMatchDetails/index';
import { useSports } from '../context/SportsProvider';
import { useIsInWebview } from '../hooks/useIsInWebview';
import CarouselsDisplay from 'containers/CarouselsDisplay';
import useMenuRedirect from 'containers/Menu/hooks/useMenuRedirect';

const MATCH_ID_PARAM = 'matchId';
const STAGE_CODE_PARAM = 'stage_code';
const DEFAULT_STAGE_CODE = 'GROUP_STAGE';
const COMBINED_STAGE_CODE = 'SEMI_FINALS, THIRD_PLACE';
const SEMI_FINALS_CODES = ['SEMI_FINALS', 'THIRD_PLACE'];

const WCFixtures = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchMatchFixture, currentStage } = useSports();
  const isInWebview = useIsInWebview();
  const { nodesSelected } = useMenuRedirect();

  const matchIdParam = useMemo(
    () => {
      const raw = searchParams.get(MATCH_ID_PARAM);
      if (raw == null || String(raw).trim() === '') return null;
      return String(raw).trim();
    },
    [searchParams],
  );

  const stageCodeParam = searchParams.get(STAGE_CODE_PARAM);
  const hasStageCode = stageCodeParam != null && stageCodeParam !== '';

  const showMatchDetails = Boolean(matchIdParam);

  useEffect(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('screen');
        if (next.get('from') !== 'country') {
          next.delete('countryId');
        }
        return next;
      },
      { replace: true },
    );
  }, []);

  useEffect(
    () => {
      if (hasStageCode) return;
      if (!currentStage.fetched) return;

      const stageCode = currentStage.stage?.code;
      const resolvedCode = SEMI_FINALS_CODES.includes(stageCode)
        ? COMBINED_STAGE_CODE
        : stageCode || DEFAULT_STAGE_CODE;

      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (!next.get(STAGE_CODE_PARAM)) {
            next.set(STAGE_CODE_PARAM, resolvedCode);
          }
          return next;
        },
        { replace: true },
      );
    },
    [hasStageCode, currentStage.fetched, currentStage.stage, setSearchParams],
  );

  useEffect(
    () => {
      if (!matchIdParam) return;
      fetchMatchFixture();
    },
    [matchIdParam, fetchMatchFixture],
  );

  const handleMatchCardClick = useCallback(
    matchId => {
      if (matchId == null || matchId === '') return;
      const params = new URLSearchParams(location.search);
      params.set(MATCH_ID_PARAM, String(matchId));
      navigate(`${location.pathname}?${params.toString()}`);
    },
    [navigate, location],
  );

  const handleBackFromDetails = useCallback(
    () => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          const from = next.get('from');
          next.delete(MATCH_ID_PARAM);

          if (from === 'country') {
            const returnCountryId = next.get('returnCountryId');
            next.delete('from');
            next.delete('returnCountryId');
            next.set('tab', 'countries');
            if (returnCountryId) {
              next.set('countryId', returnCountryId);
            }
            next.delete('stage_code');
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const isReadyToRenderCarrousels = !isInWebview && nodesSelected?.code;
  
  if (showMatchDetails) {
    return (
      <div>
        <WCMatchDetails onBack={handleBackFromDetails} />
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 48 }}>
      {hasStageCode ? (
        <Fragment>
          <WCCarousel onMatchCardClick={handleMatchCardClick} />
          {isReadyToRenderCarrousels && (
            <CarouselsDisplay customCode={'mundial2026'} />
          )}
        </Fragment>
      ) : null}
    </div>
  );
};

export default WCFixtures;
